import * as THREE from 'three';
import type { CarDefinition } from '../core/types';

export interface CarModel {
  root: THREE.Group;
  wheels: THREE.Group[];
  brakeLights: THREE.Mesh[];
  accentMaterial: THREE.MeshStandardMaterial;
  setWheelMotion(speedMps: number, steer: number, delta: number): void;
  setBraking(active: boolean): void;
}

const shared = {
  tire: new THREE.MeshStandardMaterial({ color: 0x080a0d, roughness: 0.82, metalness: 0.08 }),
  rim: new THREE.MeshStandardMaterial({ color: 0x77828f, roughness: 0.28, metalness: 0.82 }),
  dark: new THREE.MeshStandardMaterial({ color: 0x101722, roughness: 0.4, metalness: 0.55 }),
  glass: new THREE.MeshPhysicalMaterial({
    color: 0x102c43,
    roughness: 0.12,
    metalness: 0.15,
    transmission: 0.22,
    transparent: true,
    opacity: 0.86,
  }),
  headlight: new THREE.MeshStandardMaterial({ color: 0xe8fbff, emissive: 0xb9f6ff, emissiveIntensity: 4 }),
};

function box(
  name: string,
  size: [number, number, number],
  position: [number, number, number],
  material: THREE.Material,
  bevel = false,
): THREE.Mesh {
  const geometry = new THREE.BoxGeometry(...size, bevel ? 2 : 1, bevel ? 2 : 1, bevel ? 3 : 1);
  if (bevel) {
    const positionAttribute = geometry.getAttribute('position');
    const vector = new THREE.Vector3();
    for (let index = 0; index < positionAttribute.count; index += 1) {
      vector.fromBufferAttribute(positionAttribute, index);
      const taper = 1 - Math.max(0, vector.y / Math.max(size[1], 0.01)) * 0.12;
      vector.x *= taper;
      positionAttribute.setXYZ(index, vector.x, vector.y, vector.z);
    }
    geometry.computeVertexNormals();
  }
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.position.set(...position);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function wedgeGeometry(width: number, height: number, length: number, noseSlope: number): THREE.BufferGeometry {
  const halfWidth = width / 2;
  const halfLength = length / 2;
  const rearHeight = height / 2;
  const frontHeight = Math.max(height * (0.18 + (1 - noseSlope) * 0.5), height * 0.16);
  const vertices = new Float32Array([
    -halfWidth, -rearHeight, -halfLength, halfWidth, -rearHeight, -halfLength,
    -halfWidth, rearHeight, -halfLength, halfWidth, rearHeight, -halfLength,
    -halfWidth, -rearHeight, halfLength, halfWidth, -rearHeight, halfLength,
    -halfWidth * 0.94, frontHeight, halfLength, halfWidth * 0.94, frontHeight, halfLength,
  ]);
  const indices = [
    0, 2, 1, 1, 2, 3,
    4, 5, 6, 5, 7, 6,
    0, 4, 2, 2, 4, 6,
    1, 3, 5, 3, 7, 5,
    2, 6, 3, 3, 6, 7,
    0, 1, 4, 1, 5, 4,
  ];
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function makeWheel(radius: number, width: number): THREE.Group {
  const group = new THREE.Group();
  const tire = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, width, 14, 1), shared.tire);
  tire.rotation.z = Math.PI / 2;
  tire.castShadow = true;
  const rim = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.52, radius * 0.52, width * 1.03, 8), shared.rim);
  rim.rotation.z = Math.PI / 2;
  rim.castShadow = true;
  group.add(tire, rim);
  return group;
}

function addStripe(root: THREE.Group, car: CarDefinition, material: THREE.Material, bodyY: number): void {
  const { bodyWidth, bodyLength } = car.shape;
  if (car.shape.archetype === 'wedge' || car.shape.archetype === 'track') {
    const stripe = box('center-stripe', [bodyWidth * 0.12, 0.025, bodyLength * 0.82], [0, bodyY + car.shape.bodyHeight * 0.52, 0.12], material);
    root.add(stripe);
  } else {
    const left = box('side-accent-left', [0.025, 0.12, bodyLength * 0.58], [-bodyWidth * 0.505, bodyY, 0.05], material);
    const right = left.clone();
    right.name = 'side-accent-right';
    right.position.x *= -1;
    root.add(left, right);
  }
}

export function createCarModel(car: CarDefinition, preview = false): CarModel {
  const root = new THREE.Group();
  root.name = `car-${car.id}`;

  const bodyMaterial = new THREE.MeshStandardMaterial({
    color: car.primaryColor,
    roughness: 0.24,
    metalness: 0.55,
  });
  const secondaryMaterial = new THREE.MeshStandardMaterial({
    color: car.secondaryColor,
    roughness: 0.32,
    metalness: 0.48,
  });
  const accentMaterial = new THREE.MeshStandardMaterial({
    color: car.accentColor,
    emissive: car.accentColor,
    emissiveIntensity: preview ? 1.4 : 0.55,
    roughness: 0.24,
    metalness: 0.42,
  });

  const { shape } = car;
  const bodyY = shape.wheelRadius * 0.96 + shape.bodyHeight * 0.42;
  const body = new THREE.Mesh(
    wedgeGeometry(shape.bodyWidth, shape.bodyHeight, shape.bodyLength, shape.noseSlope),
    bodyMaterial,
  );
  body.name = 'body';
  body.position.y = bodyY;
  body.castShadow = true;
  body.receiveShadow = true;
  root.add(body);

  const lower = box(
    'lower-sill',
    [shape.bodyWidth * 1.03, shape.bodyHeight * 0.23, shape.bodyLength * 0.88],
    [0, shape.wheelRadius * 0.75, -shape.bodyLength * 0.02],
    secondaryMaterial,
  );
  root.add(lower);

  const cabinZ = shape.cabinOffset;
  const cabin = box(
    'cabin',
    [shape.cabinWidth, shape.cabinHeight, shape.cabinLength],
    [0, bodyY + shape.bodyHeight * 0.46 + shape.cabinHeight * 0.42, cabinZ],
    shared.glass,
    true,
  );
  root.add(cabin);

  const roof = box(
    'roof-panel',
    [shape.cabinWidth * 0.83, 0.055, shape.cabinLength * 0.66],
    [0, cabin.position.y + shape.cabinHeight * 0.5, cabinZ - shape.cabinLength * 0.05],
    secondaryMaterial,
  );
  root.add(roof);

  addStripe(root, car, accentMaterial, bodyY);

  const wheels: THREE.Group[] = [];
  const wheelX = shape.bodyWidth * 0.53;
  const frontZ = shape.bodyLength * 0.31;
  const rearZ = -shape.bodyLength * 0.31;
  for (const [x, z] of [[-wheelX, frontZ], [wheelX, frontZ], [-wheelX, rearZ], [wheelX, rearZ]] as Array<[number, number]>) {
    const wheel = makeWheel(shape.wheelRadius, shape.wheelWidth);
    wheel.position.set(x, shape.wheelRadius, z);
    wheel.userData.front = z > 0;
    root.add(wheel);
    wheels.push(wheel);
  }

  const lightWidth = shape.bodyWidth * 0.25;
  for (const side of [-1, 1]) {
    const headlight = box(
      'headlight',
      [lightWidth, shape.bodyHeight * 0.11, 0.04],
      [side * shape.bodyWidth * 0.28, bodyY + shape.bodyHeight * 0.04, shape.bodyLength * 0.505],
      shared.headlight,
    );
    root.add(headlight);
  }

  const brakeMaterial = new THREE.MeshStandardMaterial({ color: 0x5a0505, emissive: 0xff1608, emissiveIntensity: 1.6 });
  const brakeLights: THREE.Mesh[] = [];
  for (const side of [-1, 1]) {
    const tail = box(
      'brake-light',
      [lightWidth, shape.bodyHeight * 0.12, 0.04],
      [side * shape.bodyWidth * 0.28, bodyY + shape.bodyHeight * 0.02, -shape.bodyLength * 0.505],
      brakeMaterial,
    );
    root.add(tail);
    brakeLights.push(tail);
  }

  const bumperFront = box('front-splitter', [shape.bodyWidth * 0.86, 0.08, 0.18], [0, shape.wheelRadius * 0.55, shape.bodyLength * 0.51], shared.dark);
  const bumperRear = box('rear-diffuser', [shape.bodyWidth * 0.9, 0.1, 0.18], [0, shape.wheelRadius * 0.56, -shape.bodyLength * 0.51], shared.dark);
  root.add(bumperFront, bumperRear);

  if (shape.spoiler !== 'none') {
    const spoilerZ = -shape.bodyLength * 0.42;
    const spoilerY = bodyY + shape.bodyHeight * 0.62;
    if (shape.spoiler === 'wing') {
      root.add(
        box('spoiler-post-left', [0.05, 0.34, 0.05], [-shape.bodyWidth * 0.28, spoilerY, spoilerZ], shared.dark),
        box('spoiler-post-right', [0.05, 0.34, 0.05], [shape.bodyWidth * 0.28, spoilerY, spoilerZ], shared.dark),
        box('spoiler-wing', [shape.bodyWidth * 0.9, 0.08, 0.3], [0, spoilerY + 0.17, spoilerZ], accentMaterial),
      );
    } else {
      root.add(box('spoiler-lip', [shape.bodyWidth * 0.76, 0.09, 0.12], [0, spoilerY - 0.05, spoilerZ], accentMaterial));
    }
  }

  const shadowMaterial = new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.3, depthWrite: false });
  const shadow = new THREE.Mesh(new THREE.PlaneGeometry(shape.bodyWidth * 1.25, shape.bodyLength * 0.9), shadowMaterial);
  shadow.name = 'contact-shadow';
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = 0.018;
  root.add(shadow);

  return {
    root,
    wheels,
    brakeLights,
    accentMaterial,
    setWheelMotion(speedMps, steer, delta) {
      const rotation = (speedMps * delta) / Math.max(shape.wheelRadius, 0.1);
      for (const wheel of wheels) {
        const tire = wheel.children[0];
        const rim = wheel.children[1];
        tire.rotation.x += rotation;
        rim.rotation.x += rotation;
        wheel.rotation.y = wheel.userData.front ? steer * 0.38 : 0;
      }
    },
    setBraking(active) {
      brakeMaterial.emissiveIntensity = active ? 6 : 1.6;
    },
  };
}

export function disposeCarModel(model: CarModel): void {
  model.root.traverse((object) => {
    if (object instanceof THREE.Mesh) {
      object.geometry.dispose();
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      for (const material of materials) {
        if (![shared.tire, shared.rim, shared.dark, shared.glass, shared.headlight].includes(material as THREE.MeshStandardMaterial)) {
          material.dispose();
        }
      }
    }
  });
  model.root.removeFromParent();
}
